<i18n lang="yaml" locale="en">
  background color: Background color
  text color: Text color
  indicator color: Indicator color
  active color: Active color
  active: Active
  autoplay: Autoplay
  delay time (seconds): Delay time (seconds)
  font (menu): Menu font
  add font long: Add a font by dropping it here.
  add font short: Add as font.
</i18n>

<i18n lang="yaml" locale="de">
  background color: Hintergrundfarbe
  text color: Textfarbe
  indicator color: Rahmenfarbe
  active color: Aktivfarbe
  active: Aktiv
  autoplay: Autoplay
  delay time (seconds): Verzögerung (Sekunden)
  font (menu): Menü-Schriftart
  add font long: Fügen Sie eine Schriftart hinzu, indem Sie sie hier ablegen.
  add font short: Als Schriftart hinzufügen.
</i18n>

<template>
  <div
    class="slider"
    v-hammer:swipe.left.right="onSwipe"
  >
    <component
      v-for="child in children"
      :key="child.getters.path"
      :path="child.getters.path"
      :is="child.getters.type"
      v-bind="child.getters.props"
      :navigate="navigateTo"
      :transition="transition"
    />
    <div
      v-if="menuActive"
      :is="$editMode && !$localeMode ? 'slider-editor' : 'div'"
      :class="menuClasses"
    >
      <component
        v-for="child in children"
        :key="child.getters.path"
        :path="child.getters.path"
        :is="child.getters.type"
        v-bind="child.getters.props"
        :menu="true"
        :background-color="backgroundColor"
        :text-color="textColor"
        :indicator-color="indicatorColor"
        :active-color="activeColor"
        :navigate="navigateTo"
        :style="slideStyleInMenu"
      />
      <locale-dropdown
        v-if="$store.getters.defaultLocale"
        :value="activeLocale"
        :options="dropdownOptions"
        @input="updateLocale($event)"
        :background-color="backgroundColor"
        v-bind="dropdownConfig"
      />
    </div>
  </div>
</template>

<script>
import BaseModule from '@/components/modules/BaseModule'
import SliderEditor from '@/components/editor/SliderEditor'
import colors from '@/defaultColors'
import translatable from '@/store/mixins/translatable'
import fontHandler from '@/mixins/fontHandler'
import LocaleDropdown from '@/components/modules/LocaleDropdown'

export default {
  // eslint-disable-next-line vue/name-property-casing
  name: 'slider',
  extends: BaseModule,
  customStore: translatable,
  components: {
    SliderEditor,
    LocaleDropdown
  },
  mixins: [fontHandler([
    'font'
  ])],
  inject: ['$project'],
  props: {
    menuActive: {
      type: Boolean,
      default: true
    },
    // eslint-disable-next-line
    _headlineBackgroundColor: {
      input: 'heading',
      label: 'background color'
    },
    backgroundColor: {
      type: String,
      input: 'color',
      default: '#fff'
    },
    // eslint-disable-next-line
    _headlineTextColor: {
      input: 'heading',
      label: 'text color'
    },
    textColor: {
      type: String,
      input: 'color',
      default: '#000'
    },
    // eslint-disable-next-line
    _headlineIndicatorColor: {
      input: 'heading',
      label: 'indicator color'
    },
    indicatorColor: {
      type: String,
      input: 'color',
      default: '#000'
    },
    // eslint-disable-next-line
    _headlineActiveColor: {
      input: 'heading',
      label: 'active color'
    },
    activeColor: {
      type: String,
      input: 'color',
      default: colors.colorMain
    },
    // eslint-disable-next-line
    _headlineAutoplay: {
      input: 'heading',
      label: 'autoplay'
    },
    autoplay: {
      type: Boolean,
      default: false,
      input: 'checkbox',
      label: 'active'
    },
    slideChangeDelay: {
      type: Number,
      default: 10,
      input: 'number',
      label: 'delay time (seconds)'
    },
    // eslint-disable-next-line
    _headlineFont: {
      input: 'heading',
      label: 'font (menu)'
    },
    font: {
      input: 'drop',
      type: String,
      contentType: 'font',
      tooltip: 'add font short',
      placeholder: 'add font long',
      default: ''
    }
  },
  data () {
    return {
      lastSlideIndex: -1,
      transition: '',
      intervalID: null,
      boxHeight: 1
    }
  },
  computed: {
    slides () {
      return this.$store.getters.children
    },
    menuClasses () {
      return {
        menu: true,
        [this.$project.format]: true
      }
    },
    slideStyleInMenu () {
      return {
        ...this.fontStyles.font
      }
    },
    dropdownConfig () {
      return {
        height: this.boxHeight * 6,
        width: this.boxHeight * 8,
        inverse: true
      }
    },
    dropdownOptions () {
      return [...this.$store.getters.locales, this.$store.getters.defaultLocale]
    },
    activeLocale () {
      return this.$root.$store.getters.contentLocale || this.$store.getters.defaultLocale
    }
  },
  methods: {
    updateLocale (locale) {
      return this.$root.$store.dispatch('setContentLocale', {
        locale: locale === this.$store.getters.defaultLocale ? '' : locale
      })
    },
    navigateTo (name, animate = true) {
      if (animate) {
        const newSlideIndex = this.slides.map(slide => name === slide.getters.name).indexOf(true)
        if (newSlideIndex > this.lastSlideIndex) {
          this.transition = 'forward'
        } else if (newSlideIndex < this.lastSlideIndex) {
          this.transition = 'backward'
        }
      } else {
        this.transition = 'none'
      }
      // this needs to be on the next tick to prepare the transitions inside Slide.vue first
      this.$nextTick(() => {
        this.$router.push({ path: this.$store.getters.path + '/' + name })
        this.$nextTick(() => {
          this.transition = '' // reset transition, since the next step out of the slider might not be from this component
        })
      })
    },
    navigateNext () {
      if (this.slides.length) {
        const nextIndex = this.lastSlideIndex + 1
        this.transition = 'forward'
        this.$nextTick(() => {
          if (nextIndex >= this.slides.length) {
            // goto first page if reached end
            this.$router.push(this.slides[0].getters.path)
          } else {
            this.$router.push(this.slides[nextIndex].getters.path)
          }
          this.$nextTick(() => {
            this.transition = '' // reset transition, since the next step out of the slider might not be from this component
          })
        })
      }
    },
    navigatePrevious () {
      if (this.slides.length) {
        const previousIndex = this.lastSlideIndex - 1
        this.transition = 'backward'
        this.$nextTick(() => {
          if (previousIndex < 0) {
            // goto first page if reached end
            this.$router.push(this.slides[this.slides.length - 1].getters.path)
          } else {
            this.$router.push(this.slides[previousIndex].getters.path)
          }
          this.$nextTick(() => {
            this.transition = '' // reset transition, since the next step out of the slider might not be from this component
          })
        })
      }
    },
    onSwipe (event) {
      if (!this.$route.query.fullscreen && !this.$editMode) {
        switch (Math.sign(event.deltaX)) {
          case 1:
            this.navigatePrevious()
            break
          case -1:
            this.navigateNext()
            break
        }
      }
    },
    setupAutoplay () {
      this.stopAutoplay()
      if (this.autoplay) {
        this.intervalID = window.setInterval(() => {
          if (!this.$editMode) {
            this.navigateNext()
          }
        }, this.slideChangeDelay * 1000)
      }
    },
    stopAutoplay () {
      window.clearInterval(this.intervalID)
    },
    triggerClientSizeUpdate () {
      this.boxHeight = this.$el.clientHeight / (this.$project.format === 'landscape' ? 108 : 192)
    }
  },
  created () {
    window.addEventListener('resize', this.triggerClientSizeUpdate)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.triggerClientSizeUpdate)
  },
  mounted () {
    this.triggerClientSizeUpdate()
    this.setupAutoplay()
  },
  activated () {
    this.triggerClientSizeUpdate()
    this.setupAutoplay()
  },
  deactivated () {
    this.transition = ''
    this.stopAutoplay()
  },
  watch: {
    '$route.path': {
      immediate: true,
      handler (newPath) {
        this.lastSlideIndex = this.slides.map(slide => newPath.startsWith(slide.getters.path)).indexOf(true)
      }
    },
    autoplay () {
      this.setupAutoplay()
    },
    slideChangeDelay () {
      this.setupAutoplay()
    },
    isTarget: {
      immediate: true,
      handler (isTarget) {
        if (isTarget) {
          const slide = this.$store.getters.children[0]
          if (slide) {
            this.navigateTo(slide.getters.name, false)
          }
        }
      }
    }
  },
  updated () {
    if (this.isActive && this.$store.getters.path) {
      const currentChildName = this.$route.path.substr(this.$store.getters.path.length + 1).split('/')[0]
      if (!this.$store.getters.childrenByName[currentChildName]) {
        // current slide doesn't exist (anymore) - default to lastSlideIndex
        if (this.lastSlideIndex < 0) {
          // if unset default to first slide
          const slide = this.$store.getters.children[0]
          if (slide) {
            this.navigateTo(slide.getters.name, false)
          }
        } else if (this.lastSlideIndex >= this.$store.getters.children.length) {
          // if more than available childs default to last slide
          const slide = this.$store.getters.children[this.$store.getters.children.length - 1]
          if (slide) {
            this.navigateTo(slide.getters.name, false)
          }
        } else {
          const slide = this.$store.getters.children[this.lastSlideIndex]
          if (slide) {
            this.navigateTo(slide.getters.name, false)
          }
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.slider {
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  background-color: #fff;
}

$boxes-y-landscape: 108;
$h-landscape: ((100/$boxes-y-landscape) * 1%) * 6;

$boxes-y-portrait: 192;
$h-portrait: ((100/$boxes-y-portrait) * 1%) * 6;

.menu {
  cursor: pointer;
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100vw;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: flex-end;
  align-items: stretch;

  &.landscape {
    height: $h-landscape;
  }

  &.portrait {
    height: $h-portrait;
  }
}
</style>
